import type { ComponentProps, ForwardedRef } from 'react';
import React, { useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { ProductEventDataList } from 'corgi-types';
import {
  alignItems,
  backgroundColor,
  borderRadius,
  boxShadow,
  display,
  height,
  justifyContent,
  minWidth,
  opacity,
  size as styledSize,
  themeGet,
  width,
} from 'styled-system';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  BorderRadiusProps,
  BoxShadowProps,
  DisplayProps,
  HeightProps,
  JustifyContentProps,
  MinWidthProps,
  OpacityProps,
  SizeProps,
  SpaceProps,
  WidthProps,
} from 'styled-system';

import type { OutlineProps, ThemeOpacityProps } from 'common/theme/system-types';
import { useMatchesViewport } from '../../hooks/media';
import { useWishlist, useWishlistState } from '../../hooks/wishlist';
import { useLocalization } from '../../providers/LocaleProvider';
import { useWishlistContext } from '../../providers/WishlistProvider';
import { pulse } from '../../theme/keyframes';
import { cursor, outline, themeOpacity } from '../../theme/system-utilities';
import type { CursorProps } from '../../theme/system-utilities';
import type { ArticleTracking } from '../../types/Article';
import { Button } from '../Button';
import type { IconProps } from '../Icon';
import Icon from '../Icon';
import { CaptionRegularCR1 } from '../Typography';
import { dispatchWishlistEvent } from './tracking';
import { useWishlistModal } from './useWishlistModal';
import { formatWishlistCount } from './utils';
import WishlistErrorModal from './WishlistErrorModal';

interface WishlistButtonV2Props extends SizeProps, HeightProps {
  sku: string;
  index?: number;
  tracking?: ArticleTracking;
  className?: string;
  id?: string;
  list?: ProductEventDataList;
  count?: number;
  hideCount?: boolean;
}

interface WishlistIconProps extends SizeProps {
  isActive: boolean;
}

const enum IconColor {
  Inactive = 'colors.sys.neutral.icon.default',
  Hover = 'colors.sys.primary.icon.hover',
  Active = 'colors.sys.primary.icon.active',
}

const ICON_SIZE = [16, null, null, 24];

const iconAnimationCSS = ({ isActive }: WishlistIconProps) =>
  isActive &&
  css`
    animation: ${pulse} 1s ease;
  `;

const ButtonContainer = styled(Button)<
  DisplayProps &
    WidthProps &
    HeightProps &
    BorderRadiusProps &
    BackgroundColorProps &
    ThemeOpacityProps &
    AlignItemsProps &
    CursorProps &
    SpaceProps &
    BoxShadowProps &
    OutlineProps & { ref: ForwardedRef<HTMLElement> } & MinWidthProps &
    JustifyContentProps & {
      isWishlisted: boolean;
    }
>`
  ${display};
  ${width};
  ${height};
  ${borderRadius};
  ${backgroundColor};
  ${themeOpacity};
  ${outline};
  ${alignItems}
  ${boxShadow};
  ${minWidth};
  ${justifyContent};

  &:focus-visible {
    outline: 2px solid ${themeGet('colors.sys.secondary.border.focus')};
    outline-offset: 3px;
    box-shadow: ${props =>
      props.isWishlisted
        ? `0 0 0 1px ${themeGet('colors.sys.primary.border.hover')(props)}`
        : `0 0 0 1px ${themeGet('colors.sys.neutral.border.hover')(props)}`};
  }

  &:hover {
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
    outline: ${props =>
      props.isWishlisted
        ? `1px solid ${themeGet('colors.sys.primary.border.hover')(props)}`
        : `1px solid ${themeGet('colors.sys.neutral.border.hover')(props)}`};
    outline-offset: 0px;
  }

  &:active {
    background-color: ${themeGet('colors.sys.neutral.background.active')};
    outline: ${props =>
      props.isWishlisted
        ? `2px solid ${themeGet('colors.sys.primary.border.active')(props)}`
        : `2px solid ${themeGet('colors.sys.neutral.border.active')(props)}`};

    > svg {
      fill: ${props =>
        props.isWishlisted
          ? `${themeGet('colors.sys.primary.border.active')(props)}`
          : `${themeGet('colors.sys.neutral.border.hover')(props)}`};
    }
  }

  &:focus-visible:active {
    outline: 2px solid ${themeGet('colors.sys.secondary.border.focus')};
    outline-offset: 4px;
    box-shadow: ${props =>
      props.isWishlisted
        ? `0 0 0 2px ${themeGet('colors.sys.primary.border.active')(props)}`
        : `0 0 0 2px ${themeGet('colors.sys.neutral.border.hover')(props)}`};
  }
`;

const buttonContainerStyles: Partial<ComponentProps<typeof ButtonContainer>> = {
  display: 'flex',
  kind: 'bare',
  alignItems: 'center',
  bg: 'sys.neutral.background.default',
  borderRadius: 'x-large',
  cursor: 'pointer',
  p: ['sp_8', null, null, 'sp_12'],
  themeOpacity: 'strong',
  outline: 'none',
  boxShadow: 'low',
  height: 'sp_40',
  minWidth: 'sp_40',
  justifyContent: 'center',
};

const StyledIcon = styled(Icon)<IconProps & WishlistIconProps>`
  ${styledSize};
  ${iconAnimationCSS};
  fill: ${({ isActive }) => themeGet(isActive ? IconColor.Active : IconColor.Inactive)};
  &:hover {
    fill: ${themeGet(IconColor.Hover)};
  }
`;

const Count = styled(CaptionRegularCR1)<OpacityProps & CursorProps & { localCount: number }>`
  ${opacity};
  ${cursor};

  ${({ localCount }) =>
    // this condition ensures the animation runs only on the first render
    localCount === 1 &&
    css`
      transform: translateX(100%);
      animation: slideIn 0.5s forwards;

      @keyframes slideIn {
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
    `}
`;

const countStyles: Partial<ComponentProps<typeof Count>> = {
  as: 'label',
  color: 'sys.neutral.text.default',
  pl: ['sp_8', null, null, 'sp_12'],
  cursor: 'pointer',
};

const getWishlistCount = (count: number, isWishlisted: boolean) =>
  isWishlisted ? count + 1 : count;

const useGetIcon = (isActive: boolean) => {
  const {
    matches: { matchesLargeDesktop },
  } = useMatchesViewport();
  if (matchesLargeDesktop) {
    return isActive ? 'wishlistV2FilledLarge' : 'wishlistV2Large';
  }

  return isActive ? 'wishlistV2Filled' : 'wishlistV2';
};

const WishlistButtonV2 = ({
  sku,
  list,
  tracking,
  index: position,
  size = 17,
  count = 0,
  hideCount = false,
  ...props
}: WishlistButtonV2Props) => {
  const { t } = useLocalization();
  const buttonRef = useRef<HTMLElement>(null);
  const wishlistContext = useWishlistContext();
  const [isWishlisted, setIsWishlisted] = useWishlistState(sku, wishlistContext?.state);
  const wishlistCount = getWishlistCount(count, isWishlisted);
  const iconName = useGetIcon(isWishlisted);
  const { isModalOpen, errorCode, onModalClose, onError } = useWishlistModal();
  const { isInProgress, clickHandler } = useWishlist({
    sku,
    isWishlisted,
    setIsWishlisted,
    onError,
    onSuccess: (wishlistState: boolean) => {
      dispatchWishlistEvent({ wishlistState, tracking, position, list });
    },
    onFinished: () => {
      buttonRef?.current?.focus();
    },
  });

  return (
    <>
      <ButtonContainer
        ref={buttonRef}
        data-testid="wishlist-button"
        aria-label={isWishlisted ? t('h24_remove_from_wishlist') : t('h24_add_to_wishlist')}
        role="checkbox"
        aria-disabled={isInProgress}
        aria-checked={isWishlisted}
        {...buttonContainerStyles}
        {...props}
        {...(props.height && !wishlistCount ? { width: props.height, p: 0 } : {})}
        isWishlisted={isWishlisted}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          if (isInProgress) {
            return;
          }
          clickHandler(e);
        }}
      >
        <StyledIcon
          name={iconName}
          dataTestId={iconName}
          isActive={isWishlisted}
          size={size || ICON_SIZE}
        />
        {!hideCount && wishlistCount ? (
          <Count data-testid="wishlist-count" localCount={wishlistCount} {...countStyles}>
            {formatWishlistCount(wishlistCount)}
          </Count>
        ) : null}
      </ButtonContainer>
      {isModalOpen && (
        <WishlistErrorModal
          isModalOpen={isModalOpen}
          errorCode={errorCode}
          onClose={onModalClose}
        />
      )}
    </>
  );
};

export default WishlistButtonV2;
